import React from "react";
import { Document, Page, View, Text, Image } from "@react-pdf/renderer";
import { styles, statusColour } from "./pdfTheme";
import { thumbFor } from "./photos";
import {
  Cover, CoverDetails, Chrome, Heading, Narrative, ScoreAnalysis, OverallAssessment, Scope, ScoreTable,
} from "./pdfSections";

/**
 * The Standard layout — the client report as a register. Ported from
 * Csa/report/CsaStandardPdf.js: one row per finding in a landscape table,
 * roughly seven findings per page. Elaborate — one entry per finding, set
 * out in full — is ElectricalElaboratePdf.js, drawing the same model.
 *
 * No verification_method column: Electrical's checklist carries no such
 * field (elec_checkpoint has no equivalent of CSA's verification method),
 * so the composer's column set is one shorter here.
 *
 * This renderer makes no decisions. Everything it draws was already chosen
 * by the server's report composer — which findings appear, what each one
 * says. If something here needs an `if` about *whether* to print, that logic
 * is in the wrong place.
 */

/* ------------------------------------------------------------------ */
/* the register                                                        */
/* ------------------------------------------------------------------ */

function Findings({ section, header }) {
  const d = section.data;
  const cols = d.columns;
  const w = (key) => {
    const c = cols.find((x) => x.key === key);
    return { width: `${c ? c.width : 10}%` };
  };

  if (!d.rows.length) {
    return (
      <Page size="A4" orientation="landscape" style={styles.page}>
        <Chrome header={header} />
        <Heading no={section.no} title={section.title} />
        <Text style={styles.empty}>No findings were selected for this report.</Text>
      </Page>
    );
  }

  return (
    <Page size="A4" orientation="landscape" style={styles.page}>
      <Chrome header={header} />
      <Heading no={section.no} title={section.title} />
      <Text style={[styles.muted, { marginBottom: 8 }]}>
        {d.selected} finding{d.selected === 1 ? "" : "s"} shown, from {d.assessed} checkpoints
        assessed.
      </Text>

      <View style={styles.regHead} fixed>
        {cols.map((c) => (
          <Text key={c.key} style={[styles.regHeadCell, w(c.key)]}>
            {c.label.toUpperCase()}
          </Text>
        ))}
      </View>

      {d.rows.map((r, i) => (
        <View key={r.number} style={[styles.regRow, i % 2 ? styles.zebra : {}]}>
          <Text style={[styles.regCell, w("sr_no")]}>{r.number}</Text>
          <Text style={[styles.regCell, w("area")]}>{r.area}</Text>
          <Text style={[styles.regCell, w("category")]}>{r.category}</Text>

          <View style={[styles.regCell, w("status")]}>
            <Text style={[styles.statusPill, { backgroundColor: statusColour(r.status) }]}>
              {r.status}
            </Text>
          </View>

          <Text style={[styles.regCell, w("observation")]}>{r.observation}</Text>
          <Text style={[styles.regCell, w("recommendation")]}>{r.recommendation || "—"}</Text>
          <Text style={[styles.regCell, w("legal_reference")]}>{r.legal_reference || "—"}</Text>

          <View style={[styles.regCell, w("photos")]}>
            {r.photos && r.photos.length ? (
              <View style={styles.thumbRow}>
                {r.photos.slice(0, 4).map((u, k) => (
                  <Image key={k} src={thumbFor(u)} style={styles.thumb} />
                ))}
              </View>
            ) : (
              <Text>—</Text>
            )}
          </View>
        </View>
      ))}
    </Page>
  );
}

/* ------------------------------------------------------------------ */
/* the document                                                        */
/* ------------------------------------------------------------------ */

export default function ElectricalStandardPdf({ model }) {
  const { header, summary, sections } = model;
  const byKey = (k) => sections.find((s) => s.key === k);

  const renderSection = (s) => {
    if (s.key === "score_analysis") return <ScoreAnalysis key={s.key} section={s} />;
    if (s.key === "overall_assessment") return <OverallAssessment key={s.key} section={s} />;
    if (s.key === "scope") return <Scope key={s.key} section={s} />;
    if (s.key === "score_table") return <ScoreTable key={s.key} section={s} />;
    return <Narrative key={s.key} section={s} />;
  };

  // Portrait pages, in document order, with the register lifted out because it
  // is landscape — eight columns will not fit otherwise.
  const before = sections.filter((s) => s.no < 7);
  const after = sections.filter((s) => s.no > 7);

  return (
    <Document
      title={`Electrical — ${header.organization} — ${header.site}`}
      author={header.prepared_by || "Momentum"}
      subject="Electrical Audit"
    >
      <Cover header={header} summary={summary} />

      <Page size="A4" style={styles.page}>
        <Chrome header={header} />
        <CoverDetails header={header} summary={summary} />
      </Page>

      {before.map((s) => (
        <Page key={s.key} size="A4" style={styles.page}>
          <Chrome header={header} />
          {renderSection(s)}
        </Page>
      ))}

      <Findings section={byKey("findings")} header={header} />

      {after.map((s) => (
        <Page key={s.key} size="A4" style={styles.page}>
          <Chrome header={header} />
          {renderSection(s)}
        </Page>
      ))}
    </Document>
  );
}
